import {
  useState, useMemo, useRef, useEffect, useCallback
} from 'react'
import {
  Card, CardHeader, Button, Flex
} from '@binance/uikit'
import styled from 'styled-components'
import {WebSocketClient} from '@binance/js-sdk'
import useRouter from 'hooks/useRouter'
import useSymbolInfo from 'hooks/useSymbolInfo'
import {WS_ROOT} from 'constants'
import useScale from 'hooks/useScale'
import DefaultChart from './DefaultChart'
import CandlestickChart from './CandlestickChart'
import DepthChart from './DepthChart'
import OptPanel from './OptPanel'
import {getIntervals} from './Common/chart'

const ChartViewport = styled(Card)`
  display: flex;
  flex-direction: column;
  background: ${({theme}) => theme.bg};
  position: ${({fullScreen}) => (fullScreen ? 'fixed' : 'relative')};
  z-index: ${({fullScreen}) => (fullScreen ? 1000 : '')};
  overflow: hidden;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
`

const klineInterval = {'1D': '1d', '1W': '1w'}
const getKlinesWsUri = (symbolName, interval) =>
  `${WS_ROOT}?streams=${symbolName.toLowerCase()}@kline_${klineInterval[interval] || interval}`

export default function GraphView ({i18n}) {
  const lastIntervalRef = useRef()
  const lastWsRef = useRef()
  const lastView = useRef(/* 'default' */)
  const [interval, setIntervals] = useState('')
  const [view, setView] = useState(/* 'default' */)
  const [size, setSize] = useState({width: 388, height: 200})
  const [indicator, setIndicator] = useState(view === 'default')
  const [indName, setIndName] = useState('')
  const [lineType, setLineType] = useState('Candle')
  const [dataSetup, setDataSetup] = useState(0)
  const [lastKlineUpdate, setLastKlineUpdate] = useState({})
  const [klineSetting, setKlineSetting] = useState({key: 'kline', isLoading: true})
  const [curIndicator, setCurIndicator] = useState({})
  const [fullScreen, setFullScreen] = useState(false)
  const scale = useScale()

  const setCurIndicators = useCallback(
    ({curInd, curOver}) => {
      setCurIndicator({
        curInd: curInd !== undefined ? curInd : curIndicator.curInd,
        curOver: curOver !== undefined ? curOver : curIndicator.curOver
      })
    },
    [curIndicator]
  )

  const handleFullScreen = useCallback(() => {
    setFullScreen(!fullScreen)
  }, [fullScreen])

  const containerRef = useRef(null)
  const router = useRouter()
  const {symbol, lng} = router.query
  const {symbolInfo} = useSymbolInfo()
  const {quoteAsset} = symbolInfo

  useEffect(() => {
    const chartLineType = window.localStorage.getItem('chart.lineType')
    if (chartLineType) setLineType(chartLineType)
    if (chartLineType === 'Candle') {
      const chartInterval = window.localStorage.getItem('chart.interval') || '1m'
      lastIntervalRef.current = chartInterval
      setIntervals(chartInterval)
    }
    const chartView = window.localStorage.getItem('chart.view')
    if (chartView && chartView !== 'undefined') setView(chartView)
    else {
      lastView.current = 'default'
      setView('default')
    }
  }, [])

  useEffect(() => {
    if (view !== 'default' && lastWsRef.current) {
      // lastWsRef.current.close(1e3, 'Requested by Default Chart')
    }
  }, [view])

  useEffect(() => {
    if (interval) setLineType('Candle')
    if (lastIntervalRef.current !== interval) window.localStorage.setItem('chart.interval', interval)
    if (lastView.current !== view) window.localStorage.setItem('chart.view', view)
    if (lineType) window.localStorage.setItem('chart.lineType', lineType)
  }, [interval, lineType, view])

  useEffect(() => {
    if (
      view !== 'default'
      || (!lastWsRef.current && !interval)
      || (lastView.current === view && lastIntervalRef.current === interval)
    ) {
      return
    }
    setLastKlineUpdate({})
    lastWsRef.current && lastWsRef.current.close(1e3, 'Requested by Default Chart')
    const timer = setTimeout(() => {
      clearTimeout(timer)
      lastIntervalRef.current = interval
      lastWsRef.current = WebSocketClient(getKlinesWsUri(symbol, interval || '1m'))
      lastWsRef.current.onmessage(evt => {
        const {data} = JSON.parse(evt.data)
        setLastKlineUpdate({[interval]: data})
      })
      lastWsRef.current.open()
    }, 0)

    return () => {
      setDataSetup(dataSetup + 1)
      lastWsRef.current && lastWsRef.current.close(1e3, 'Requested by Default Chart')
    }
  }, [dataSetup, interval, symbol, view])

  useEffect(() => {
    lastIntervalRef.current = interval
    lastView.current = view
  }, [interval, view])

  useEffect(() => {
    if (!fullScreen) setSize({width: 0, height: 0}) // reset to init
    setTimeout(() => {
      const {width, height} = containerRef?.current?.getBoundingClientRect()
      setSize({width, height: height - 40})
    }, 0)
  }, [fullScreen])

  return useMemo(
    () => (
      <ChartViewport fullScreen={fullScreen} gridArea="chart" className="graph-view-pc">
        <Card
          ref={containerRef}
          style={{display: 'flex', flexDirection: 'column', height: 'inherit'}}
        >
          <CardHeader>
            {view !== 'depth' ? (
              <OptPanel
                interval={interval}
                setIntervals={setIntervals}
                setIndicator={setIndicator}
                setIndName={setIndName}
                lineType={lineType}
                setLineType={setLineType}
                i18n={i18n}
                view={view}
                curIndicator={curIndicator}
                setCurIndicator={setCurIndicators}
              />
            ) : null}

            <Flex flex={1} />

            <Button
              variant="small"
              glossy={false}
              onClick={() => setView('default')}
              color={view === 'default' ? 'primary' : undefined}
            >
              {i18n['graph-defaultChart'] || 'Original'}
            </Button>
            <Button
              variant="small"
              glossy={false}
              onClick={() => setView('tradingView')}
              color={view === 'tradingView' ? 'primary' : undefined}
            >
              {i18n['graph-tradingView'] || 'TradingView'}
            </Button>
            <Button
              variant="small"
              glossy={false}
              onClick={() => setView('depth')}
              color={view === 'depth' ? 'primary' : undefined}
            >
              {i18n['graph-depth'] || 'Depth'}
            </Button>
            <Button variant="small" glossy={false} onClick={handleFullScreen}>
              <img src={require('assets/expand.svg')} style={{width: 14, height: 14}} />
            </Button>
          </CardHeader>
          {view === 'default' /* && lastIntervalRef.current !== interval  */ && (
            <DefaultChart
              hidden={view !== 'default'}
              selectedView="default"
              fullScreen={fullScreen}
              locale={lng}
              symbol={symbol}
              interval={getIntervals[interval]}
              intervalName={interval}
              lineType={lineType}
              indicatorName={indName}
              drawLineType={lineType}
              dataSetup={dataSetup}
              quoteAsset={quoteAsset}
              tickSize={0.01}
              minQty={0.000001}
              isDataLoading={klineSetting.isLoading}
              curIndicators={curIndicator}
              setDataIsLoading={setKlineSetting}
              setCurIndicator={setCurIndicators}
              lastKlineUpdate={lastKlineUpdate}
              height={size.height}
              pro={true}
            />
          )}
          {view === 'tradingView' && (
            <CandlestickChart
              interval={getIntervals[interval]}
              showIndicatorsDialog={indicator}
              hidden={view !== 'tradingView'}
              lineType={lineType}
            />
          )}
          {view === 'depth' && <DepthChart scale={scale} size={size} hidden={view !== 'depth'} />}
        </Card>
      </ChartViewport>
    ),
    [
      curIndicator,
      dataSetup,
      fullScreen,
      handleFullScreen,
      i18n,
      indName,
      indicator,
      interval,
      klineSetting.isLoading,
      lastKlineUpdate,
      lineType,
      lng,
      quoteAsset,
      scale,
      setCurIndicators,
      size,
      symbol,
      view
    ]
  )
}
